import { faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Page } from '~/components/Page'
import { useViewer } from '../api'
import { useMfaRecoveryCodes, useMfaStatus } from '../api/useMfa'
import { MfaChallengeForm, MfaEnrolment, RecoveryCodes } from '../components'

/**
 * Two-factor setup and verification for staff.
 *
 * The Django admin middleware redirects here with ?next=, and RequireMfa sends
 * staff here when they reach Team Tools without a verified session. One page
 * covers both because what a user needs depends on server state, not on which
 * route sent them: enrol if there is no device, verify if there is one.
 */
export const MfaPage = () => {
    const [searchParams] = useSearchParams()
    const next = searchParams.get('next')

    const { data: viewer } = useViewer()
    const { data: status, isLoading } = useMfaStatus()
    const regenerate = useMfaRecoveryCodes()

    // Once enrolment begins this pins the page to the enrolment component until
    // the user explicitly finishes. Without it, confirming enrolment flips
    // session_verified, this page would switch branch, and the freshly issued
    // recovery codes would be unmounted before they were ever read -- they are
    // shown once and cannot be retrieved.
    const [enrolling, setEnrolling] = useState(false)
    const [newCodes, setNewCodes] = useState<string[] | null>(null)

    const goBack = () => {
        // A full navigation, not a router push: `next` usually points at the
        // Django admin, which is not part of this SPA.
        window.location.href = next || '/dashboard'
    }

    const body = () => {
        if (isLoading || !status) {
            return (
                <div className='flex flex-row items-center justify-center gap-2 p-8'>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span className='text-base font-normal'>Checking your account…</span>
                </div>
            )
        }

        // Checked before anything else, so no status change can interrupt it.
        if (enrolling) {
            return (
                <MfaEnrolment username={viewer?.username} onComplete={goBack} onStarted={() => setEnrolling(true)} />
            )
        }

        if (!status.is_staff_account) {
            return (
                <p className='text-base font-normal text-center max-w-md'>
                    Two-factor authentication applies to staff accounts. Yours does not need it.
                </p>
            )
        }

        if (status.session_verified) {
            if (newCodes) {
                return (
                    <section className='flex flex-col items-center gap-6 w-full max-w-xl'>
                        <header className='text-2xl uppercase font-black text-center'>New recovery codes</header>
                        <RecoveryCodes codes={newCodes} username={viewer?.username} />
                        <button
                            type='button'
                            onClick={() => setNewCodes(null)}
                            className='p-4 w-full md:w-1/2 hover:outline'
                            style={{ background: 'linear-gradient(142deg,#03CEFF 0%,#C91FFF 100%)' }}
                        >
                            I have saved my codes
                        </button>
                    </section>
                )
            }

            return (
                <section className='flex flex-col items-center gap-6 w-full max-w-xl'>
                    <FontAwesomeIcon icon={faCheckCircle} className='text-5xl text-green-400' />
                    <p className='text-base font-normal text-center'>
                        This session is verified.
                        {status.unused_recovery_codes === 0
                            ? ' You have no recovery codes left.'
                            : ` You have ${status.unused_recovery_codes} recovery codes remaining.`}
                    </p>

                    <button
                        type='button'
                        onClick={goBack}
                        className='p-4 w-full md:w-1/2 hover:outline'
                        style={{ background: 'linear-gradient(142deg,#03CEFF 0%,#C91FFF 100%)' }}
                    >
                        Continue
                    </button>

                    {/* Recovery codes are shown once, so there has to be a way to
                        get a fresh set — otherwise losing them means losing the
                        only route back in from a lost phone. */}
                    <button
                        type='button'
                        disabled={regenerate.isLoading}
                        onClick={() =>
                            regenerate.mutate(undefined, { onSuccess: (data) => setNewCodes(data.recovery_codes) })
                        }
                        className='flex flex-row gap-2 items-center text-base font-normal underline hover:no-underline'
                    >
                        <span>Generate new recovery codes</span>
                        {regenerate.isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
                    </button>
                    <label className='login-form error text-center'>{regenerate.error?.message}</label>
                </section>
            )
        }

        // Enrolled but this session has not verified: it predates the rollout, or
        // has gone stale past MFA_SESSION_MAX_AGE.
        if (status.enrolled) {
            return (
                <section className='flex flex-col items-center gap-6 w-full max-w-xl'>
                    <header className='text-2xl uppercase font-black text-center'>Verify it is you</header>
                    <MfaChallengeForm />
                </section>
            )
        }

        return <MfaEnrolment username={viewer?.username} onComplete={goBack} onStarted={() => setEnrolling(true)} />
    }

    return (
        <Page title='TWO-FACTOR' header='TWO-FACTOR'>
            <section className='animate-fade-in px-8 w-full max-w-4xl mx-auto flex flex-col items-center gap-y-8 text-xl text-center font-bold py-16 lg:py-24'>
                {body()}
            </section>
        </Page>
    )
}
